import { Component, type ErrorInfo, type ReactNode } from "react";
import { Button } from "@mui/material";
import * as Sentry from "@sentry/react";
import { t } from "i18next";
import { UnknownErrorPage } from "./fallbacks/unknown-error-page/unknown-error-page";
import { Flex } from "@/components/layout/flex/flex";

interface Props {
	children: ReactNode;
	fallback?: ReactNode;
}

interface State {
	hasError: boolean;
	error: Error | null;
}

export function ErrorActions() {
	function handleReset() {
		window.location.href = "/";
	}

	return (
		<Flex mt={2} gap={2} justifyContent="center">
			<Button>{t("Visit our help center")}</Button>
			<Button onClick={handleReset} variant="contained" color="primary">
				{t("Back to application")}
			</Button>
		</Flex>
	);
}

export class RootErrorBoundary extends Component<Props, State> {
	public constructor(props: Props) {
		super(props);
		this.state = { hasError: false, error: null };
	}

	public static getDerivedStateFromError(error: Error): State {
		return { hasError: true, error };
	}

	public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
		console.error("Root Error Boundary caught error:", error, errorInfo);

		// Report the error to Sentry with React error boundary context
		Sentry.captureException(error, {
			contexts: {
				react: {
					componentStack: errorInfo.componentStack,
				},
				errorBoundary: {
					boundaryName: "RootErrorBoundary",
				},
			},
			tags: {
				errorBoundary: "root",
			},
		});
	}

	public render() {
		if (this.state.hasError) {
			if (this.props.fallback) {
				return this.props.fallback;
			}

			return <UnknownErrorPage actions={<ErrorActions />} />;
		}

		return this.props.children;
	}
}
